import { useCallback, useEffect } from 'react'
import { PaneBlock, useBlockActionStore } from '@editorStores'

/**
 * Role a caller plays for a given block action:
 * - `provider`: registers a block for `action` while mounted (e.g. a dirty/invalid form).
 *   The block is removed on unmount or when `type` becomes `undefined`.
 * - `subscriber`: only reads whether `action` is currently blocked.
 * - `undefined`: no-op provider — nothing is registered.
 */
export type BlockActionType = 'provider' | 'subscriber' | undefined

export interface UseBlockActionResult {
  /** Reactive flag — `true` while any provider blocks `action`. */
  isBlocked: boolean
  /** Imperative read of the latest blocked state, e.g. inside event handlers. */
  isBlockedFn: () => boolean
}

/**
 * Coordinate "can this action happen?" across unrelated components via the global
 * `useBlockActionStore`.
 *
 * A provider registers a block while a condition holds:
 *   useBlockAction('anchor-grid', isDirty ? 'provider' : undefined)
 *
 * A subscriber checks whether the action is blocked before running it (e.g. closing a form):
 *   const { isBlocked, isBlockedFn } = useBlockAction('anchor-grid', 'subscriber')
 *
 * A provider that passes a `paneBlock` also refuses leaving the drawer pane of the current
 * selection (other element/mode/tab, ESC, storey or 2D/3D switch) while it blocks: the selection
 * store and the storey switch check it centrally, so no exit needs its own subscriber. Pass a
 * stable (module-level) object, the block is re-registered when it changes.
 */
const useBlockAction = (
  action: string,
  type?: BlockActionType,
  paneBlock?: PaneBlock,
): UseBlockActionResult => {
  const addBlockedAction = useBlockActionStore(state => state.addBlockedAction)
  const removeBlockedAction = useBlockActionStore(state => state.removeBlockedAction)

  const isProvider = type === 'provider'

  useEffect(() => {
    if (!isProvider) return

    addBlockedAction(action, paneBlock)

    return () => removeBlockedAction(action)
  }, [action, isProvider, paneBlock, addBlockedAction, removeBlockedAction])

  // Subscribe only to *this* action's blocked flag (a boolean), never the whole `blockedActions`
  // Set. Subscribing to the Set re-renders every block-action consumer — Results2, the 3D scene,
  // every list row, the detail panes — whenever any unrelated action toggles, which the previous
  // ref-based guard never did. A narrow boolean selector only re-renders on this action's flips.
  const isBlocked = useBlockActionStore(state => state.blockedActions.has(action))

  const isBlockedFn = useCallback(
    () => useBlockActionStore.getState().blockedActions.has(action),
    [action],
  )

  return { isBlocked, isBlockedFn }
}

/**
 * Reports whether _any_ action is currently blocked (any provider is registered). Useful for
 * global gates like the left sidebar page switcher that must stop navigation on any unsaved change.
 */
export const useIsAnyActionBlocked = (): UseBlockActionResult => {
  // Narrow to a boolean so this only re-renders when crossing the empty ↔ non-empty boundary,
  // not on every add/remove of the underlying Set.
  const isBlocked = useBlockActionStore(state => state.blockedActions.size > 0)

  const isBlockedFn = useCallback(() => useBlockActionStore.getState().blockedActions.size > 0, [])

  return { isBlocked, isBlockedFn }
}

export default useBlockAction
